import { computed, shallowRef, watch, type Ref } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  getMessage,
  listConversations,
  searchMessages,
} from "./tradeChatLogsApi";
import type {
  ChatConversationPage,
  ChatMessageDetail,
  ChatMessagePage,
  ChatMessageQuery,
} from "./tradeChatLogsTypes";

type Status = "idle" | "loading" | "ready" | "error";
interface MessageListState {
  status: Status;
  page: ChatMessagePage;
  loadingMore: boolean;
  moreFailed: boolean;
}
const PAGE_SIZE = 100;
const DETAIL_CONTEXT = 8;
const emptyPage = (): ChatMessagePage => ({
  items: [],
  total: 0,
  has_more: false,
  next_cursor: null,
});

/**
 * Cursor-paginates messages within one filter snapshot. A filter change aborts in-flight
 * requests, so a slow response for an old search can never overwrite the current one.
 */
export function useChatMessageList(query: Ref<ChatMessageQuery>) {
  const { globalErrorHandler } = useGlobalHandlers();
  const state = shallowRef<MessageListState>({
    status: "idle",
    page: emptyPage(),
    loadingMore: false,
    moreFailed: false,
  });
  const key = computed(() => JSON.stringify(query.value));
  const retryCount = shallowRef(0);
  let loadNextPage: () => Promise<void> = async () => {};

  watch(
    [key, retryCount],
    (_, __, onCleanup) => {
      const params = query.value;
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      state.value = {
        status: "loading",
        page: emptyPage(),
        loadingMore: false,
        moreFailed: false,
      };

      async function load(append: boolean) {
        const cursor = append ? state.value.page.next_cursor : null;
        if (
          controller.signal.aborted ||
          (append && (!cursor || state.value.loadingMore))
        )
          return;
        state.value = {
          ...state.value,
          loadingMore: append,
          moreFailed: false,
        };
        try {
          const page = await searchMessages(
            { ...params, limit: PAGE_SIZE, cursor: cursor ?? undefined },
            controller.signal
          );
          if (controller.signal.aborted) return;
          state.value = {
            status: "ready",
            page: {
              ...page,
              items: append
                ? [...state.value.page.items, ...page.items]
                : page.items,
            },
            loadingMore: false,
            moreFailed: false,
          };
        } catch (error) {
          if (controller.signal.aborted) return;
          state.value = {
            ...state.value,
            status: append ? "ready" : "error",
            loadingMore: false,
            moreFailed: append,
          };
          globalErrorHandler(error);
        }
      }
      loadNextPage = () => load(true);
      void load(false);
    },
    { immediate: true }
  );

  return {
    state,
    loadMore: () => loadNextPage(),
    retry: () => retryCount.value++,
  };
}

/** Conversations for the sidebar, filtered server-side by name. */
export function useChatConversations(search: Ref<string>) {
  const { globalErrorHandler } = useGlobalHandlers();
  const state = shallowRef<{ status: Status; page: ChatConversationPage }>({
    status: "idle",
    page: { items: [], total: 0, has_more: false },
  });
  const retryCount = shallowRef(0);
  watch(
    [search, retryCount],
    async ([q], _, onCleanup) => {
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      state.value = { ...state.value, status: "loading" };
      try {
        const page = await listConversations(
          { q: q.trim() || undefined, limit: 500 },
          controller.signal
        );
        if (!controller.signal.aborted) state.value = { status: "ready", page };
      } catch (error) {
        if (controller.signal.aborted) return;
        state.value = { ...state.value, status: "error" };
        globalErrorHandler(error);
      }
    },
    { immediate: true }
  );
  return { state, retry: () => retryCount.value++ };
}

/** The open message with its context; only the current selection may publish a result. */
export function useChatMessageDetail(selectedId: Ref<number | null>) {
  const { globalErrorHandler } = useGlobalHandlers();
  const state = shallowRef<{ status: Status; data: ChatMessageDetail | null }>({
    status: "idle",
    data: null,
  });
  const retryCount = shallowRef(0);
  watch(
    [selectedId, retryCount],
    async ([id], _, onCleanup) => {
      state.value = { status: id ? "loading" : "idle", data: null };
      if (!id) return;
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      try {
        const data = await getMessage(id, DETAIL_CONTEXT, controller.signal);
        if (!controller.signal.aborted) state.value = { status: "ready", data };
      } catch (error) {
        if (controller.signal.aborted) return;
        state.value = { status: "error", data: null };
        globalErrorHandler(error);
      }
    },
    { immediate: true }
  );
  return { state, retry: () => retryCount.value++ };
}
